/**
 * 商家控制台「我的小程序」面板
 *
 * 商家自助维护 B 端小程序基本信息（AppID、主体、主题、功能模块），
 * 提交打包申请 → 运营审批 → 构建。
 *
 * 权威: docs/platform/b-miniapp/通用型B端小程序设计方案.md §6
 * BFF:   /api/v1/b-miniapp/* (fit1/api/b_miniapp_router.py)
 */
import { $, bindClick } from "../lib/dom.js";
import { escHtml } from "../lib/hints.js";
import { api } from "../api.js";
import { showBanner } from "./index.js";

const CORE_MODULES = [
  { key: "core-auth", label: "登录与身份", sub: "微信登录 / 批发客户登记" },
  { key: "core-me", label: "我的", sub: "个人中心 / 客服 / 关于" },
];

const JOB_STATUS_LABEL = {
  pending_ops: "待运营审批",
  approved: "已审批·待构建",
  running: "构建中",
  done: "已上传体验版",
  failed: "构建失败",
};

let tenantCache = null;

function canEdit() {
  if (!window.McAuth || !window.McAuth.hasPerm) return true;
  return window.McAuth.hasPerm("mc:bminiapp:write");
}

function showMsg(text, ok) {
  showBanner(text, ok);
  const el = $("bm-feedback");
  if (!el) return;
  el.textContent = text || "";
  el.classList.toggle("hidden", !text);
  el.classList.toggle("ok", !!ok);
  el.classList.toggle("err", !!text && !ok);
}

export async function loadBminiappPanel() {
  const host = $("bm-form-host");
  if (!host) return;
  host.innerHTML = '<p class="mc-desc">加载中…</p>';
  const { ok, data } = await api("GET", "/api/v1/b-miniapp/tenant");
  if (!ok || !data) {
    host.innerHTML = '<p class="mc-desc err">加载失败</p>';
    return;
  }
  tenantCache = data;
  renderForm(data);
  await Promise.all([loadJobs(), loadPendingCorp()]);
}

const COLOR_PRESETS = [
  { name: "品牌红", value: "#c45c5c" },
  { name: "朱砂红", value: "#c23a3a" },
  { name: "珊瑚橘", value: "#e67e50" },
  { name: "暖杏色", value: "#c48b6e" },
  { name: "黛蓝", value: "#2c5f7c" },
  { name: "墨青", value: "#3a7d7a" },
  { name: "竹青", value: "#5a8a6a" },
  { name: "松绿", value: "#4a7c59" },
  { name: "鸦黑", value: "#333333" },
  { name: "凝夜紫", value: "#6a4c7c" },
];

const colorSwatches = (current, disAttr) => {
  const exact = COLOR_PRESETS.find((c) => c.value.toLowerCase() === (current || "").toLowerCase());
  const custom = exact ? "" : " selected";
  return `
    <div class="bm-swatch-list">
      ${COLOR_PRESETS.map((c) => {
        const active = c.value.toLowerCase() === (current || "").toLowerCase() ? " selected" : "";
        return `<button type="button" class="bm-swatch${active}" data-color="${c.value}" title="${c.name} ${c.value}" ${disAttr}>
          <span class="bm-swatch-dot" style="background:${c.value}"></span>
          <span class="bm-swatch-name">${c.name}</span>
        </button>`;
      }).join("")}
      <button type="button" class="bm-swatch bm-swatch-custom${custom}" data-color="" title="自定义" ${disAttr}>
        <span class="bm-swatch-dot" style="background:conic-gradient(from 45deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000);border:1px solid #ddd"></span>
        <span class="bm-swatch-name">自定义</span>
      </button>
    </div>`;
};

function bindLogoPreview() {
  const input = $("bm-disp-logo");
  const preview = document.querySelector(".bm-logo-preview");
  if (!input || !preview) return;
  const render = () => {
    const v = input.value.trim();
    if (!v) {
      preview.innerHTML = "<em>暂无预览</em>";
      return;
    }
    preview.innerHTML = `<img src="${escHtml(v)}" alt="" onerror="this.nextElementSibling.style.display='flex';this.style.display='none'" /><span class="bm-logo-error" style="display:none;color:#a33;font-size:0.7rem">预览失败</span>`;
  };
  input.addEventListener("input", render);
}

function bindColorSwatches() {
  const input = $("bm-disp-primary");
  const list = document.querySelector(".bm-swatch-list");
  if (!list || !input) return;
  list.addEventListener("click", (e) => {
    const btn = e.target.closest(".bm-swatch");
    if (!btn) return;
    const val = btn.getAttribute("data-color") || "";
    if (!val) return;
    input.value = val;
    list.querySelectorAll(".bm-swatch").forEach((b) => b.classList.remove("selected"));
    btn.classList.add("selected");
  });
  input.addEventListener("input", () => {
    const val = (input.value || "").toLowerCase();
    let matched = false;
    list.querySelectorAll(".bm-swatch[data-color]").forEach((b) => {
      const selected = b.getAttribute("data-color").toLowerCase() === val;
      b.classList.toggle("selected", selected);
      matched = matched || selected;
    });
    list.querySelector(".bm-swatch-custom")?.classList.toggle("selected", !matched && !!input.value);
  });
}

function renderForm(t) {
  const host = $("bm-form-host");
  const editable = canEdit();
  const dis = editable ? "" : "disabled";
  const d = t.display || {};
  const w = t.wechat || {};
  const f = t.features || {};
  const audit = t.audit || {};

  const moduleRow = (key, label, sub, { required = false } = {}) => {
    const m = f[key] || { enabled: false };
    const checked = required || m.enabled ? "checked" : "";
    const lock = required ? "disabled" : dis;
    const reqCls = required ? " is-required" : "";
    const badge = required ? '<span class="bm-module-badge">必选</span>' : "";
    return `
      <div class="bm-module-row${reqCls}">
        <label class="bm-module-toggle">
          <input type="checkbox" id="bm-feat-${key}" ${checked} ${lock} />
          <span>
            <span class="bm-module-name">${label}${badge}</span>
            ${sub ? `<span class="bm-module-sub">${sub}</span>` : ""}
          </span>
        </label>
      </div>`;
  };

  host.innerHTML = `
    <div class="bm-section">
      <h3>微信主体</h3>
      <div class="bm-grid">
        <label>微信 AppID *
          <input id="bm-wx-appid" type="text" value="${escHtml(w.appid || "")}" placeholder="wx..." ${dis} />
        </label>
        <label>主体名称
          <input id="bm-wx-subject" type="text" value="${escHtml(w.subject || "")}" placeholder="营业执照主体" ${dis} />
        </label>
        <label>支付商户号
          <input id="bm-wx-paymchid" type="text" value="${escHtml(w.payMerchantId || "")}" ${dis} />
        </label>
        <label>合法域名
          <input id="bm-wx-domain" type="text" value="${escHtml(w.legalDomain || "")}" ${dis} />
        </label>
      </div>
    </div>

    <div class="bm-section">
      <h3>展示信息</h3>
      <div class="bm-grid">
        <label>小程序名称
          <input id="bm-disp-name" type="text" value="${escHtml(d.appName || "")}" ${dis} />
        </label>
        <label class="bm-logo-field">品牌 Logo
          <input id="bm-disp-logo" type="text" value="${escHtml(d.logo || "")}" placeholder="https://…/logo.png 或 assets/tenants/…/logo.png" ${dis} />
          <span class="bm-logo-preview">${d.logo ? `<img src="${escHtml(d.logo)}" alt="" onerror="this.style.display='none'" />` : "<em>暂无预览</em>"}</span>
        </label>
        <label>导航栏标题
          <input id="bm-disp-navtitle" type="text" value="${escHtml(d.navigationBarTitleText || "")}" ${dis} />
        </label>
        <label>主题主色
          <div class="bm-color-picker">
            ${colorSwatches((d.theme||{}).primary || "", dis)}
            <input id="bm-disp-primary" type="text" value="${escHtml((d.theme||{}).primary || "")}" placeholder="#c45c5c" ${dis} />
          </div>
        </label>
        <label>客服电话
          <input id="bm-disp-phone" type="text" value="${escHtml(d.servicePhone || "")}" ${dis} />
        </label>
      </div>
    </div>

    <div class="bm-section">
      <h3>上线模块</h3>
      <div class="bm-modules">
        ${CORE_MODULES.map((m) => moduleRow(m.key, m.label, m.sub, { required: true })).join("")}
        ${moduleRow("order", "订货", "色码矩阵 / 箱规 / 锁库")}
        ${moduleRow("retail", "零售上货", "上游货上到本店")}
        ${moduleRow("dist", "分销", "分销客户管理")}
        ${moduleRow("customer", "客户档案", "档案与信用")}
      </div>
    </div>

    <p class="bm-status">最近上传 <code>${escHtml(audit.lastVersion || "—")}</code> · ${escHtml(audit.lastUploadedAt || "—")}</p>
    <p id="bm-feedback" class="mc-banner hidden" role="status" aria-live="polite"></p>
    <div class="bm-actions">
      <button type="button" id="btn-bm-save" ${dis}>保存配置</button>
      <button type="button" id="btn-bm-submit" ${dis}>提交打包申请</button>
      <button type="button" class="secondary" id="btn-bm-refresh">刷新</button>
    </div>
  `;

  bindClick("btn-bm-save", saveConfig);
  bindClick("btn-bm-submit", submitBuild);
  bindClick("btn-bm-refresh", loadBminiappPanel);
  bindColorSwatches();
  bindLogoPreview();
}

function collectForm() {
  const feats = {};
  ["order", "retail", "dist", "customer"].forEach((k) => {
    const el = $("bm-feat-" + k);
    feats[k] = { enabled: !!(el && el.checked) };
  });
  // 订货模块保留原有子开关
  if (tenantCache && tenantCache.features && tenantCache.features.order) {
    feats.order = { ...tenantCache.features.order, ...feats.order };
  }
  return {
    display: {
      appName: $("bm-disp-name").value.trim(),
      navigationBarTitleText: $("bm-disp-navtitle").value.trim(),
      theme: { primary: $("bm-disp-primary").value.trim() || "#c45c5c" },
      logo: $("bm-disp-logo").value.trim(),
      servicePhone: $("bm-disp-phone").value.trim(),
    },
    wechat: {
      appid: $("bm-wx-appid").value.trim(),
      subject: $("bm-wx-subject").value.trim(),
      payMerchantId: $("bm-wx-paymchid").value.trim(),
      legalDomain: $("bm-wx-domain").value.trim(),
    },
    features: feats,
  };
}

async function saveConfig() {
  const body = collectForm();
  const { ok, data } = await api("PUT", "/api/v1/b-miniapp/tenant", { body });
  if (ok) {
    tenantCache = data;
    showMsg("配置已保存", true);
  } else {
    showMsg((data && (data.detail || data.message)) || "保存失败", false);
  }
}

async function submitBuild() {
  // 先保存再提交，避免提交了未保存的配置
  const saved = await saveConfig();
  const t = tenantCache || {};
  const version = (t.build || {}).version || "1.0.0";
  const { ok, data } = await api("POST", "/api/v1/b-miniapp/build/jobs", {
    body: { version, note: "" },
  });
  if (ok) {
    showMsg(`已提交打包申请 v${version}，待运营审批`, true);
    await loadJobs();
  } else {
    showMsg((data && (data.detail || data.message)) || "提交失败", false);
  }
}

async function loadJobs() {
  const wrap = $("bm-jobs-host");
  if (!wrap) return;
  const { ok, data } = await api("GET", "/api/v1/b-miniapp/build/jobs");
  if (!ok || !data) {
    wrap.innerHTML = '<p class="mc-desc">加载历史失败</p>';
    return;
  }
  const items = data.items || [];
  if (!items.length) {
    wrap.innerHTML = '<p class="mc-desc">暂无打包记录</p>';
    return;
  }
  const shortTime = (s) => {
    if (!s) return "—";
    return String(s).replace("T", " ").slice(0, 16);
  };
  const cards = items.slice().reverse().map((j) => {
    const st = JOB_STATUS_LABEL[j.status] || j.status;
    const cls = j.status === "done" ? "live" : j.status === "failed" ? "bad" : "warn";
    const note = (j.build_log || "").trim().slice(0, 48);
    return `<article class="bm-job-card">
      <span class="ver">${escHtml(j.version)}</span>
      <span class="status ${cls}">${escHtml(st)}</span>
      <span>${escHtml(j.uploaded_version || "—")}</span>
      <span class="meta">提交 ${escHtml(shortTime(j.submitted_at))} · 审批 ${escHtml(shortTime(j.approved_at))}${note ? " · " + escHtml(note) : ""}</span>
    </article>`;
  }).join("");
  wrap.innerHTML = `<div class="bm-job-list">${cards}</div>`;
}

async function loadPendingCorp() {
  const wrap = $("bm-corp-host");
  if (!wrap) return;
  const { ok, data } = await api("GET", "/api/v1/b-miniapp/order/hq/pending-corp");
  if (!ok || !data) {
    wrap.innerHTML = '<p class="mc-desc">加载待确认失败</p>';
    return;
  }
  const items = data.items || [];
  if (!items.length) {
    wrap.innerHTML = '<p class="mc-desc">暂无对公待确认</p>';
    return;
  }
  const editable = canEdit();
  const cards = items.map((o) => {
    const who = [o.company, o.contact].filter(Boolean).join(" · ") || o.buyer_id || "—";
    const btn = editable
      ? `<button type="button" class="bm-corp-confirm" data-order="${escHtml(o.order_id)}">确认到账</button>`
      : "";
    return `<article class="bm-job-card">
      <span class="ver">${escHtml(o.order_id)}</span>
      <span class="status warn">${escHtml(o.status || "待确认")}</span>
      <span>¥${escHtml(String(o.total_amount ?? "—"))}</span>
      <span class="meta">${escHtml(who)} · ${escHtml(String(o.created_at || "").replace("T", " ").slice(0, 16))}</span>
      ${btn}
    </article>`;
  }).join("");
  wrap.innerHTML = `<div class="bm-job-list">${cards}</div>`;
  wrap.querySelectorAll(".bm-corp-confirm").forEach((btn) => {
    btn.addEventListener("click", () => confirmCorp(btn.getAttribute("data-order") || ""));
  });
}

async function confirmCorp(orderId) {
  if (!orderId) return;
  const { ok, data } = await api("POST", "/api/v1/b-miniapp/order/pay/corp/confirm", {
    body: { order_id: orderId },
  });
  if (ok) {
    showMsg(`已确认到账 ${orderId}`, true);
    await loadPendingCorp();
  } else {
    showMsg((data && (data.detail || data.message || (data.error && data.error.message))) || "确认失败", false);
  }
}

export function initBminiappHandlers() {
  // 按钮通过 bindClick 在 renderForm 里绑定；此处只做一次性挂载
}
